/**
 * Typed API client for DocuStudio backend.
 *
 * All requests go through the studio worker which proxies /v1/* and /v2/*
 * to docustudio-api via Service Binding (production) or vite proxy (dev).
 */

import type {
  AnnotationKind,
  AnnotationRange,
  DocListItem,
  DocMetadata,
  HighlightColor,
  Notebook,
  NotebookItem,
  RetrievedItem,
  Section,
} from "../types/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  const requestId = r.headers.get("x-docustudio-request-id") ?? undefined;
  if (!r.ok) {
    let code = "http_error";
    let msg = r.statusText;
    try {
      const body = (await r.json()) as { error?: { code: string; message: string } };
      code = body.error?.code ?? code;
      msg = body.error?.message ?? msg;
    } catch {
      /* noop */
    }
    throw new ApiError(r.status, code, msg, requestId);
  }
  return (await r.json()) as T;
}

async function requestForm<T>(path: string, body: FormData): Promise<T> {
  const r = await fetch(path, {
    method: "POST",
    credentials: "include",
    body,
  });
  const requestId = r.headers.get("x-docustudio-request-id") ?? undefined;
  if (!r.ok) {
    let code = "http_error";
    let msg = r.statusText;
    try {
      const payload = (await r.json()) as { error?: { code: string; message: string } };
      code = payload.error?.code ?? code;
      msg = payload.error?.message ?? msg;
    } catch {
      /* noop */
    }
    throw new ApiError(r.status, code, msg, requestId);
  }
  return (await r.json()) as T;
}

export interface ListDocsParams {
  project?: string;
  area?: string;
  kind?: string;
  lang?: string;
  limit?: number;
  cursor?: string;
}

export interface IngestJobResponse {
  job_id: string;
  source_object_id: string;
  status: "queued" | "converting" | "classified" | "indexed" | "failed" | "quarantined";
  source_type?: "github" | "email" | "upload" | "paste" | "url";
  original_filename?: string;
  size_bytes?: number;
  detected_project?: string | null;
  detected_lang?: string | null;
  confidence?: number | null;
  target_doc_id?: string | null;
  error_json?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CollectionSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  owner_subject: string | null;
  visibility: string;
  /** B5.1 — optional grouping for the sidebar tree. */
  folder_path: string | null;
  /** B5.1 — 0/1; 1 means user pinned. */
  pinned: number;
  created_at: string;
  updated_at: string;
  item_count: number;
}

export interface CollectionDetail extends Omit<CollectionSummary, "item_count"> {
  tags: string[];
  aggregates: {
    items_total: number;
    documents: number;
    notebooks: number;
    tags: Array<{ tag: string; count: number }>;
  };
}

export interface CollectionItem {
  membership_id: string;
  item_type: "document" | "notebook" | "asset" | "inline_note";
  item_id: string;
  /** C5 — inline_note body. NULL para items que apuntan a docs/notebooks. */
  inline_text: string | null;
  is_primary: number;
  order_index: number | null;
  created_at: string;
  doc_title: string | null;
  doc_path: string | null;
  notebook_title: string | null;
}

export interface HighlightInboxRow {
  id: string;
  doc_id: string;
  section_id: string | null;
  version_id: string;
  author_subject: string;
  kind: "highlight" | "comment" | "tag";
  color: HighlightColor | null;
  body: string | null;
  range_json: string;
  created_at: string;
  doc: {
    title: string | null;
    path: string | null;
    project: string | null;
    tags: string[];
  };
}

export interface Annotation {
  id: string;
  doc_id: string;
  section_id: string | null;
  version_id: string;
  author_subject: string;
  kind: AnnotationKind;
  color: HighlightColor | null;
  body: string | null;
  range_json: string;
  thread_id: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export const api = {
  health: () => request<{ status: string; environment: string }>("/health"),

  listDocs: (p: ListDocsParams = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v != null) sp.set(k, String(v));
    const qs = sp.toString();
    return request<{ items: DocListItem[]; next_cursor: string | null }>(
      `/v1/docs${qs ? `?${qs}` : ""}`,
    );
  },

  getDoc: (id: string) => request<DocMetadata>(`/v1/docs/${encodeURIComponent(id)}`),

  getDocSections: (id: string) =>
    request<{ items: Section[] }>(`/v1/docs/${encodeURIComponent(id)}/sections`),

  getDocMarkdown: async (id: string): Promise<string> => {
    const r = await fetch(`/v1/docs/${encodeURIComponent(id)}/markdown`, {
      credentials: "include",
    });
    if (!r.ok) throw new ApiError(r.status, "http_error", r.statusText);
    return r.text();
  },

  getSection: (id: string) =>
    request<Section & { doc_path: string; doc_title: string }>(
      `/v1/sections/${encodeURIComponent(id)}`,
    ),

  search: (body: {
    query: string;
    filters?: Record<string, unknown>;
    answerLang?: string;
    topK?: number;
  }) =>
    request<{
      items: RetrievedItem[];
      facets: Record<string, Record<string, number>>;
      plan: unknown;
      retrieval_diagnostics: unknown;
      request_id: string;
    }>("/v2/search", { method: "POST", body: JSON.stringify(body) }),

  listNotebooks: () => request<{ items: Notebook[] }>("/v2/notebooks"),

  createNotebook: (body: {
    title: string;
    description?: string;
    scope?: "personal" | "project" | "team";
    project_id?: string;
  }) =>
    request<{ id: string }>("/v2/notebooks", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getNotebook: (id: string) =>
    request<Notebook & { items: NotebookItem[] }>(`/v2/notebooks/${encodeURIComponent(id)}`),

  addNotebookItem: (
    notebookId: string,
    body: {
      item_type: string;
      source_doc_id?: string;
      source_section_id?: string;
      source_version_id?: string;
      selected_text?: string;
      note_text?: string;
      title?: string;
      metadata?: Record<string, unknown>;
    },
  ) =>
    request<{ id: string; order_index: number }>(
      `/v2/notebooks/${encodeURIComponent(notebookId)}/items`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  composeNotebook: (
    notebookId: string,
    body: { title: string; style: string; lang?: string; destination?: string },
  ) =>
    request<{ id: string; status: string }>(
      `/v2/notebooks/${encodeURIComponent(notebookId)}/compose`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // B2.5 — patch/delete/reorder notebook items
  patchNotebookItem: (
    notebookId: string,
    itemId: string,
    body: { note_text?: string; selected_text?: string; title?: string },
  ) =>
    request<{ status: string }>(
      `/v2/notebooks/${encodeURIComponent(notebookId)}/items/${encodeURIComponent(itemId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  removeNotebookItem: (notebookId: string, itemId: string) =>
    request<{ status: string }>(
      `/v2/notebooks/${encodeURIComponent(notebookId)}/items/${encodeURIComponent(itemId)}`,
      { method: "DELETE" },
    ),

  reorderNotebookItems: (notebookId: string, items: { id: string; order_index: number }[]) =>
    request<{ status: string }>(`/v2/notebooks/${encodeURIComponent(notebookId)}/items/order`, {
      method: "PATCH",
      body: JSON.stringify({ items }),
    }),

  listAnnotations: (docId: string) =>
    request<{ items: Annotation[] }>(`/v2/docs/${encodeURIComponent(docId)}/annotations`),

  getAnnotationVersion: (docId: string) =>
    request<{ version: number }>(`/v2/docs/${encodeURIComponent(docId)}/annotation-version`),

  listPresence: (docId: string) =>
    request<{
      items: Array<{ subject: string; displayName?: string; lastSeen: string }>;
    }>(`/v2/docs/${encodeURIComponent(docId)}/presence`),

  heartbeatPresence: (
    docId: string,
    body: { sectionId?: string; offset?: number; displayName?: string },
  ) =>
    request<{ status: string }>(`/v2/docs/${encodeURIComponent(docId)}/presence`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  createAnnotation: (
    docId: string,
    body: {
      kind: AnnotationKind;
      color?: HighlightColor;
      body?: string;
      range: AnnotationRange;
      section_id?: string;
      version_id: string;
      thread_id?: string;
    },
  ) =>
    request<{ id: string }>(`/v2/docs/${encodeURIComponent(docId)}/annotations`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteAnnotation: (id: string) =>
    request<{ status: string }>(`/v2/annotations/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  replyAnnotation: (id: string, body: string) =>
    request<{ id: string; thread_id: string }>(
      `/v2/annotations/${encodeURIComponent(id)}/replies`,
      { method: "POST", body: JSON.stringify({ body }) },
    ),

  getPreferences: () => request<Record<string, unknown>>("/v2/me/preferences"),

  setPreferences: (body: Record<string, unknown>) =>
    request<{ status: string }>("/v2/me/preferences", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  getLastNotebook: () =>
    request<{ notebook_id: string | null }>("/v2/me/preferences/last-notebook"),

  setLastNotebook: (notebookId: string) =>
    request<{ status: string }>("/v2/me/preferences/last-notebook", {
      method: "PUT",
      body: JSON.stringify({ notebook_id: notebookId }),
    }),

  getRecentCollections: () => request<{ slugs: string[] }>("/v2/me/preferences/recent-collections"),

  setRecentCollections: (slugs: string[]) =>
    request<{ status: string }>("/v2/me/preferences/recent-collections", {
      method: "PUT",
      body: JSON.stringify({ slugs }),
    }),

  getSearchFacets: () =>
    request<{
      collections: Array<{ value: string; label: string; count: number }>;
      projects: Array<{ value: string; count: number }>;
      kinds: Array<{ value: string; count: number }>;
      langs: Array<{ value: string; count: number }>;
      status: Array<{ value: string; count: number }>;
      tags: Array<{ tag: string; count: number }>;
    }>("/v2/search/facets"),

  // C3.1 — notifications endpoint
  listNotifications: (params: { unread?: boolean; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.unread) sp.set("unread", "true");
    if (params.limit) sp.set("limit", String(params.limit));
    return request<{
      items: Array<{
        id: string;
        kind: string;
        title: string;
        body: string | null;
        payload_json: string | null;
        read_at: string | null;
        created_at: string;
      }>;
    }>(`/v2/notifications/me${sp.toString() ? `?${sp.toString()}` : ""}`);
  },

  markNotificationsRead: (body: { ids?: string[]; all?: boolean }) =>
    request<{ status: string }>("/v2/notifications/me/read", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // C4 — summary 6 estilos
  getDocSummary: (
    docId: string,
    params: { type?: string; lang?: string; section_id?: string } = {},
  ) => {
    const sp = new URLSearchParams();
    if (params.type) sp.set("type", params.type);
    if (params.lang) sp.set("lang", params.lang);
    if (params.section_id) sp.set("section_id", params.section_id);
    return request<{
      id: string;
      content: string;
      confidence: number | null;
      model: string;
      prompt_version: string;
      created_at: string;
    }>(`/v1/docs/${encodeURIComponent(docId)}/summary${sp.toString() ? `?${sp.toString()}` : ""}`);
  },

  summarizeDoc: (docId: string, body: { type: string; lang?: string; section_id?: string }) =>
    request<{ status: string; queued?: boolean }>(
      `/v1/docs/${encodeURIComponent(docId)}/summarize`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // C4 — entities NER (simple: extrae menciones de doc_mentions o autolinks)
  getDocEntities: (docId: string) =>
    request<{
      items: Array<{ kind: string; label: string; count: number }>;
    }>(`/v1/docs/${encodeURIComponent(docId)}/entities`),

  // C4 — related docs minimal endpoint
  listDocRelated: (docId: string) =>
    request<{
      items: Array<{
        doc_id: string;
        title: string;
        path: string;
        score: number;
        reason: string;
      }>;
    }>(`/v1/docs/${encodeURIComponent(docId)}/related`),

  // C3.2 — versions endpoint
  listDocVersions: (docId: string) =>
    request<{
      items: Array<{
        id: string;
        doc_id: string;
        source_type: string;
        git_sha: string | null;
        git_branch: string | null;
        content_hash: string;
        committed_at: string | null;
        created_at: string;
      }>;
    }>(`/v1/docs/${encodeURIComponent(docId)}/versions`),

  // B6.4 — graph endpoint (semantic edges with optional scope + depth + min_conf).
  getGraph: (
    params: {
      scope?: "doc" | "col" | "proj" | "all";
      project_id?: string;
      collection_id?: string;
      depth?: number;
      min_confidence?: number;
    } = {},
  ) => {
    const sp = new URLSearchParams();
    if (params.scope) sp.set("scope", params.scope);
    if (params.project_id) sp.set("project_id", params.project_id);
    if (params.collection_id) sp.set("collection_id", params.collection_id);
    if (params.depth != null) sp.set("depth", String(params.depth));
    if (params.min_confidence != null) sp.set("min_confidence", String(params.min_confidence));
    return request<{
      nodes: Array<{
        type: string;
        id: string;
        label: string;
        project: string | null;
      }>;
      edges: Array<{
        id: string;
        source_type: string;
        source_id: string;
        target_type: string;
        target_id: string;
        relation: string;
        weight: number;
        confidence: number | null;
        source: string;
      }>;
      scope: string;
      depth: number;
      min_confidence: number;
    }>(`/v2/graph${sp.toString() ? `?${sp.toString()}` : ""}`);
  },

  listCollections: (params: { tag?: string; owner?: string; q?: string } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
    const qs = sp.toString();
    return request<{ items: CollectionSummary[] }>(`/v2/collections${qs ? `?${qs}` : ""}`);
  },

  getCollection: (slugOrId: string) =>
    request<CollectionDetail>(`/v2/collections/${encodeURIComponent(slugOrId)}`),

  getCollectionItems: (
    slugOrId: string,
    params: { type?: "document" | "notebook" | "asset" | "all"; page?: number } = {},
  ) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null) sp.set(k, String(v));
    const qs = sp.toString();
    return request<{ items: CollectionItem[]; page: number; page_size: number }>(
      `/v2/collections/${encodeURIComponent(slugOrId)}/items${qs ? `?${qs}` : ""}`,
    );
  },

  createCollection: (body: {
    title: string;
    slug?: string;
    description?: string;
    visibility?: "public" | "internal" | "restricted" | "private";
    tags?: string[];
  }) =>
    request<{ id: string; slug: string }>("/v2/collections", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // B5.1 — pin / unpin / folder
  updateCollection: (
    slugOrId: string,
    body: {
      title?: string;
      description?: string;
      visibility?: "public" | "internal" | "restricted" | "private";
      folder_path?: string | null;
      pinned?: boolean;
    },
  ) =>
    request<{ status: string }>(`/v2/collections/${encodeURIComponent(slugOrId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  addCollectionMember: (
    slugOrId: string,
    body: {
      item_type: "document" | "notebook" | "asset" | "inline_note";
      item_id?: string;
      inline_text?: string;
      is_primary?: boolean;
      order_index?: number;
    },
  ) =>
    request<{ id: string }>(`/v2/collections/${encodeURIComponent(slugOrId)}/members`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // C5 — patch / remove member (inline_text edit + reorder + delete)
  patchCollectionMember: (
    slugOrId: string,
    membershipId: string,
    body: { inline_text?: string; order_index?: number },
  ) =>
    request<{ status: string }>(
      `/v2/collections/${encodeURIComponent(slugOrId)}/members/${encodeURIComponent(membershipId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  removeCollectionMember: (slugOrId: string, membershipId: string) =>
    request<{ status: string }>(
      `/v2/collections/${encodeURIComponent(slugOrId)}/members/${encodeURIComponent(membershipId)}`,
      { method: "DELETE" },
    ),

  addCollectionTag: (slugOrId: string, tag: string) =>
    request<{ status: string }>(`/v2/collections/${encodeURIComponent(slugOrId)}/tags`, {
      method: "POST",
      body: JSON.stringify({ tag }),
    }),

  listHighlights: (params: {
    q?: string;
    kind?: "highlight" | "comment" | "tag";
    color?: "yellow" | "green" | "pink" | "blue" | "purple";
    doc_id?: string;
    project?: string;
    tag?: string;
    from?: string;
    to?: string;
    page?: number;
    page_size?: number;
  }) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null && v !== "") sp.set(k, String(v));
    const qs = sp.toString();
    return request<{
      items: HighlightInboxRow[];
      page: number;
      page_size: number;
      request_id: string;
    }>(`/v2/highlights${qs ? `?${qs}` : ""}`);
  },

  exportHighlightsUrl: (params: Record<string, string | undefined>): string => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
    const qs = sp.toString();
    return `/v2/highlights/export${qs ? `?${qs}` : ""}`;
  },

  telemetry: (event: { kind: string; [k: string]: unknown }) =>
    request<{ status: string }>("/v2/telemetry/event", {
      method: "POST",
      body: JSON.stringify(event),
    }).catch(() => undefined),

  uploadIntakeFile: (file: File, projectHint?: string) => {
    const body = new FormData();
    body.set("file", file);
    if (projectHint?.trim()) body.set("project_hint", projectHint.trim());
    return requestForm<IngestJobResponse>("/v2/intake/upload", body);
  },

  pasteIntake: (body: { text: string; filename?: string; project_hint?: string }) =>
    request<IngestJobResponse>("/v2/intake/paste", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getIngestJob: (id: string) =>
    request<IngestJobResponse>(`/v2/intake/jobs/${encodeURIComponent(id)}`),
};
